import { AnalyticsConfig, AnalyticsVisualization, KpiConfig, PerformanceCardConfig, TableConfig, FilterConfig } from '../types/analytics.types';

export interface ValidationError {
  level: 'error' | 'warning' | 'info';
  code: string;
  message: string;
  path: string;
  suggestion?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  info: ValidationError[];
}

export class AnalyticsConfigValidator {
  
  /**
   * Main validation entry point
   */
  static validate(config: AnalyticsConfig): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    const info: ValidationError[] = [];

    // Basic structure validation
    this.validateBasicStructure(config, errors);
    
    // Tab-level validation
    if (config.tabs) {
      config.tabs.forEach((tab, tabIndex) => {
        this.validateTab(tab, tabIndex, config, errors, warnings, info);
      });
    }

    // Cross-reference validation
    this.validateCrossReferences(config, errors, warnings);

    // Data source pattern validation (critical for preventing "no data" issues)
    this.validateDataSourcePatterns(config, errors, warnings);

    // Data structure validation
    this.validateDataStructure(config, errors, warnings, info);

    // Filter field validation (prevent filter field mismatches with schema)
    this.validateFilterFields(config, errors, warnings);

    // Performance and best practices
    this.validateBestPractices(config, warnings, info);

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      info
    };
  }

  /**
   * Validate basic configuration structure
   */
  private static validateBasicStructure(config: AnalyticsConfig, errors: ValidationError[]): void {
    if (!config.id) {
      errors.push({
        level: 'error',
        code: 'CONFIG_MISSING_ID',
        message: 'Configuration must have an id',
        path: 'config.id'
      });
    }

    if (!config.name) {
      errors.push({
        level: 'error',
        code: 'CONFIG_MISSING_NAME',
        message: 'Configuration must have a name',
        path: 'config.name'
      });
    }

    if (!config.tabs || config.tabs.length === 0) {
      errors.push({
        level: 'error',
        code: 'CONFIG_NO_TABS',
        message: 'Configuration must have at least one tab',
        path: 'config.tabs'
      });
    }
  }

  /**
   * Validate individual tab configuration
   */
  private static validateTab(
    tab: any, 
    tabIndex: number, 
    config: AnalyticsConfig, 
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const tabPath = `config.tabs[${tabIndex}]`;

    if (!tab.id) {
      errors.push({
        level: 'error',
        code: 'TAB_MISSING_ID',
        message: 'Tab must have an id',
        path: `${tabPath}.id`
      });
    }

    if (!tab.name) {
      errors.push({
        level: 'error',
        code: 'TAB_MISSING_NAME',
        message: 'Tab must have a name',
        path: `${tabPath}.name`
      });
    }

    // Validate visualizations
    if (tab.visualizations) {
      tab.visualizations.forEach((viz: any, vizIndex: number) => {
        this.validateVisualization(viz, `${tabPath}.visualizations[${vizIndex}]`, config, errors, warnings, info);
      });
    }

    // Validate sub-tabs
    if (tab.subTabs) {
      tab.subTabs.forEach((subTab: any, subTabIndex: number) => {
        this.validateTab(subTab, subTabIndex, config, errors, warnings, info);
      });
    }
  }

  /**
   * Validate individual visualization configuration
   */
  private static validateVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    
    // Basic validation
    if (!viz.id) {
      errors.push({
        level: 'error',
        code: 'VIZ_MISSING_ID',
        message: 'Visualization must have an id',
        path: `${vizPath}.id`
      });
    }

    if (!viz.type) {
      errors.push({
        level: 'error',
        code: 'VIZ_MISSING_TYPE',
        message: 'Visualization must have a type',
        path: `${vizPath}.type`
      });
      return; // Can't continue without type
    }

    // Type-specific validation
    switch (viz.type) {
      case 'kpi':
        this.validateKpiVisualization(viz, vizPath, config, errors, warnings, info);
        break;
      case 'performance-card':
        this.validatePerformanceCardVisualization(viz, vizPath, config, errors, warnings, info);
        break;
      case 'chart':
        this.validateChartVisualization(viz, vizPath, config, errors, warnings, info);
        break;
      case 'table':
        this.validateTableVisualization(viz, vizPath, config, errors, warnings, info);
        break;
      case 'filter':
        this.validateFilterVisualization(viz, vizPath, config, errors, warnings, info);
        break;
      case 'date-range-filter':
        this.validateDateRangeFilterVisualization(viz, vizPath, config, errors, warnings, info);
        break;
    }

    // Layout validation
    if (viz.layout) {
      if (viz.layout.span && (viz.layout.span < 1 || viz.layout.span > 12)) {
        warnings.push({
          level: 'warning',
          code: 'VIZ_INVALID_SPAN',
          message: 'Layout span should be between 1 and 12',
          path: `${vizPath}.layout.span`,
          suggestion: 'Use span values between 1-12 for proper grid layout'
        });
      }
    }
  }

  /**
   * Validate KPI-specific configuration
   */
  private static validateKpiVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const kpiConfig = viz.config as KpiConfig;
    
    if (!kpiConfig) {
      errors.push({
        level: 'error',
        code: 'KPI_MISSING_CONFIG',
        message: 'KPI visualization must have config',
        path: `${vizPath}.config`
      });
      return;
    }

    if (!kpiConfig.primaryMetric) {
      errors.push({
        level: 'error',
        code: 'KPI_MISSING_PRIMARY_METRIC',
        message: 'KPI must have a primaryMetric',
        path: `${vizPath}.config.primaryMetric`
      });
    }

    // Validate calculated/formula mode requirements
    if (kpiConfig.mode === 'calculated' || kpiConfig.mode === 'formula') {
      
      // CRITICAL: Must have dataQuery for SmartDataManager processing
      if (!viz.dataQuery) {
        errors.push({
          level: 'error',
          code: 'KPI_CALCULATED_MISSING_DATAQUERY',
          message: 'Calculated/formula KPIs must have a dataQuery object for SmartDataManager processing',
          path: `${vizPath}.dataQuery`,
          suggestion: 'Add dataQuery with configId, metrics, groupBy, and limit properties'
        });
      }

      if (!kpiConfig.sourceVisualizationId) {
        errors.push({
          level: 'error',
          code: 'KPI_CALCULATED_MISSING_SOURCE',
          message: 'Calculated/formula KPIs must have sourceVisualizationId',
          path: `${vizPath}.config.sourceVisualizationId`
        });
      }

      if (kpiConfig.mode === 'calculated' && !kpiConfig.aggregation) {
        errors.push({
          level: 'error',
          code: 'KPI_CALCULATED_MISSING_AGGREGATION',
          message: 'Calculated KPIs must have aggregation (sum, avg, min, max, count)',
          path: `${vizPath}.config.aggregation`
        });
      }

      if (kpiConfig.mode === 'formula' && !kpiConfig.formula) {
        errors.push({
          level: 'error',
          code: 'KPI_FORMULA_MISSING_FORMULA',
          message: 'Formula KPIs must have formula configuration',
          path: `${vizPath}.config.formula`
        });
      }

      // Check for empty exampleResponse (best practice for calculated KPIs)
      if (viz.exampleResponse && viz.exampleResponse.length > 0) {
        warnings.push({
          level: 'warning',
          code: 'KPI_CALCULATED_HAS_EXAMPLE_DATA',
          message: 'Calculated/formula KPIs should have empty exampleResponse arrays',
          path: `${vizPath}.exampleResponse`,
          suggestion: 'Set exampleResponse to [] for calculated KPIs to ensure they use calculated values'
        });
      }
    }

    // Server mode validation
    if (kpiConfig.mode === 'server' || !kpiConfig.mode) {
      if (!viz.dataQuery && (!viz.exampleResponse || viz.exampleResponse.length === 0)) {
        warnings.push({
          level: 'warning',
          code: 'KPI_SERVER_NO_DATA_SOURCE',
          message: 'Server KPIs should have either dataQuery or exampleResponse',
          path: `${vizPath}`,
          suggestion: 'Add dataQuery for live data or exampleResponse for mock data'
        });
      }
    }
  }

  /**
   * Validate Performance Card-specific configuration
   */
  private static validatePerformanceCardVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const cardConfig = viz.config as PerformanceCardConfig;
    
    if (!cardConfig) {
      errors.push({
        level: 'error',
        code: 'CARD_MISSING_CONFIG',
        message: 'Performance card must have config',
        path: `${vizPath}.config`
      });
      return;
    }

    if (!cardConfig.metricField) {
      errors.push({
        level: 'error',
        code: 'CARD_MISSING_METRIC_FIELD',
        message: 'Performance card must have metricField',
        path: `${vizPath}.config.metricField`
      });
    }

    if (!cardConfig.rankingType) {
      errors.push({
        level: 'error',
        code: 'CARD_MISSING_RANKING_TYPE',
        message: 'Performance card must have rankingType (highest or lowest)',
        path: `${vizPath}.config.rankingType`
      });
    }

    // Same calculated mode validation as KPIs
    if (cardConfig.mode === 'calculated') {
      if (!viz.dataQuery) {
        errors.push({
          level: 'error',
          code: 'CARD_CALCULATED_MISSING_DATAQUERY',
          message: 'Calculated performance cards must have dataQuery object',
          path: `${vizPath}.dataQuery`,
          suggestion: 'Add dataQuery for SmartDataManager processing'
        });
      }

      if (!cardConfig.sourceVisualizationId) {
        errors.push({
          level: 'error',
          code: 'CARD_CALCULATED_MISSING_SOURCE',
          message: 'Calculated performance cards must have sourceVisualizationId',
          path: `${vizPath}.config.sourceVisualizationId`
        });
      }
    }
  }

  /**
   * Validate Table-specific configuration
   */
  private static validateTableVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const tableConfig = viz.config as TableConfig;
    
    if (!tableConfig) {
      errors.push({
        level: 'error',
        code: 'TABLE_MISSING_CONFIG',
        message: 'Table must have config',
        path: `${vizPath}.config`
      });
      return;
    }

    if (!tableConfig.columns || tableConfig.columns.length === 0) {
      errors.push({
        level: 'error',
        code: 'TABLE_MISSING_COLUMNS',
        message: 'Table must have at least one column',
        path: `${vizPath}.config.columns`
      });
    }

    // Validate hierarchical table requirements
    if (tableConfig.hierarchical) {
      if (!viz.dataQuery?.hierarchyLevels || viz.dataQuery.hierarchyLevels.length === 0) {
        errors.push({
          level: 'error',
          code: 'TABLE_HIERARCHICAL_MISSING_LEVELS',
          message: 'Hierarchical tables must have hierarchyLevels in dataQuery',
          path: `${vizPath}.dataQuery.hierarchyLevels`
        });
      }
    }

    // Validate data source - CRITICAL: Tables cannot render without data
    if (!viz.dataQuery && (!viz.exampleResponse || viz.exampleResponse.length === 0)) {
      errors.push({
        level: 'error',
        code: 'TABLE_NO_DATA_SOURCE',
        message: 'Table must have either dataQuery or exampleResponse to display data',
        path: `${vizPath}`,
        suggestion: 'Add dataQuery for calculated data or exampleResponse for static mock data'
      });
    }
  }

  /**
   * Validate Chart-specific configuration
   */
  private static validateChartVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const chartConfig = viz.config as any; // ChartConfig is complex with unions
    
    if (!chartConfig) {
      errors.push({
        level: 'error',
        code: 'CHART_MISSING_CONFIG',
        message: 'Chart must have config',
        path: `${vizPath}.config`
      });
      return;
    }

    if (!chartConfig.chartType) {
      errors.push({
        level: 'error',
        code: 'CHART_MISSING_TYPE',
        message: 'Chart must have chartType (line, bar, pie, etc.)',
        path: `${vizPath}.config.chartType`
      });
    }

    // Validate aggregation configuration mismatch (THIS IS THE KEY VALIDATION)
    const hasGroupByInDataQuery = viz.dataQuery?.groupBy && viz.dataQuery.groupBy.length > 0;
    const hasCalculatedMode = chartConfig.mode === 'calculated';
    const hasGroupByInConfig = chartConfig.groupBy && chartConfig.groupBy.length > 0;
    const hasAggregation = chartConfig.aggregation;

    if (hasGroupByInDataQuery && !hasCalculatedMode) {
      errors.push({
        level: 'error',
        code: 'CHART_GROUPBY_NEEDS_CALCULATED_MODE',
        message: 'Charts with groupBy in dataQuery must have mode: "calculated" in config',
        path: `${vizPath}.config.mode`,
        suggestion: 'Add mode: "calculated" to enable chart aggregation'
      });
    }

    if (hasCalculatedMode && hasGroupByInDataQuery && !hasGroupByInConfig) {
      warnings.push({
        level: 'warning',
        code: 'CHART_CALCULATED_MISSING_GROUPBY_CONFIG',
        message: 'Calculated charts should repeat groupBy in config for clarity',
        path: `${vizPath}.config.groupBy`,
        suggestion: 'Add groupBy array to config matching dataQuery.groupBy'
      });
    }

    if (hasCalculatedMode && hasGroupByInDataQuery && !hasAggregation) {
      warnings.push({
        level: 'warning',
        code: 'CHART_CALCULATED_MISSING_AGGREGATION',
        message: 'Calculated charts should specify aggregation method',
        path: `${vizPath}.config.aggregation`,
        suggestion: 'Add aggregation: "sum" | "avg" | "count" | etc.'
      });
    }

    // Validate pie/doughnut chart limitations
    if (chartConfig.chartType === 'pie' || chartConfig.chartType === 'doughnut') {
      if (hasGroupByInDataQuery && viz.dataQuery.groupBy.length > 1) {
        warnings.push({
          level: 'warning',
          code: 'PIE_CHART_MULTIPLE_GROUPBY',
          message: 'Pie/doughnut charts work best with single groupBy dimension',
          path: `${vizPath}.dataQuery.groupBy`,
          suggestion: 'Consider using bar chart for multiple dimensions'
        });
      }

      // This would have caught our exact issue!
      if (hasGroupByInDataQuery && !hasCalculatedMode) {
        errors.push({
          level: 'error',
          code: 'PIE_CHART_NEEDS_AGGREGATION',
          message: 'Pie/doughnut charts with groupBy must use calculated mode to aggregate data',
          path: `${vizPath}.config.mode`,
          suggestion: 'Add mode: "calculated" and aggregation: "sum" to prevent chart crash'
        });
      }

      // NEW: Validate dataKey field availability
      const hasYAxis = chartConfig.yAxis && chartConfig.yAxis.field;
      const hasMetrics = chartConfig.metrics && chartConfig.metrics.length > 0;
      const hasSeries = chartConfig.series && chartConfig.series.length > 0 && chartConfig.series[0].field;
      
      if (!hasYAxis && !hasMetrics && !hasSeries) {
        warnings.push({
          level: 'warning',
          code: 'PIE_CHART_MISSING_DATA_FIELD',
          message: 'Pie/doughnut charts should specify which field to use for slice values',
          path: `${vizPath}.config`,
          suggestion: 'Add yAxis.field, metrics array, or series with field property to specify data field'
        });
      }
    }

    // Validate time series requirements
    if (chartConfig.mode === 'calculated' && chartConfig.dateFilterId) {
      if (!chartConfig.metrics || chartConfig.metrics.length === 0) {
        warnings.push({
          level: 'warning',
          code: 'CHART_TIME_SERIES_MISSING_METRICS',
          message: 'Time series charts should specify metrics to aggregate',
          path: `${vizPath}.config.metrics`,
          suggestion: 'Add metrics array with field names to aggregate'
        });
      }
    }

    // Validate data source - CRITICAL: Charts cannot render without data
    if (!viz.dataQuery && (!viz.exampleResponse || viz.exampleResponse.length === 0)) {
      errors.push({
        level: 'error',
        code: 'CHART_NO_DATA_SOURCE',
        message: 'Chart must have either dataQuery or exampleResponse to display data',
        path: `${vizPath}`,
        suggestion: 'Add dataQuery for calculated data or exampleResponse for static mock data'
      });
    }
  }

  /**
   * Validate Filter-specific configuration
   */
  private static validateFilterVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const filterConfig = viz.config as FilterConfig;
    
    if (!filterConfig) {
      errors.push({
        level: 'error',
        code: 'FILTER_MISSING_CONFIG',
        message: 'Filter must have config',
        path: `${vizPath}.config`
      });
      return;
    }

    if (!filterConfig.filterId) {
      errors.push({
        level: 'error',
        code: 'FILTER_MISSING_FILTER_ID',
        message: 'Filter must have filterId',
        path: `${vizPath}.config.filterId`
      });
    }

    if (filterConfig.type === 'select' && (!filterConfig.options || filterConfig.options.length === 0)) {
      warnings.push({
        level: 'warning',
        code: 'FILTER_SELECT_NO_OPTIONS',
        message: 'Select filters should have options defined',
        path: `${vizPath}.config.options`
      });
    }
  }

  /**
   * Validate Date Range Filter-specific configuration
   */
  private static validateDateRangeFilterVisualization(
    viz: AnalyticsVisualization, 
    vizPath: string, 
    config: AnalyticsConfig,
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const filterConfig = viz.config as any;
    
    if (!filterConfig?.filterId) {
      errors.push({
        level: 'error',
        code: 'DATE_FILTER_MISSING_FILTER_ID',
        message: 'Date range filter must have filterId',
        path: `${vizPath}.config.filterId`
      });
    }
  }

  /**
   * Validate cross-references between visualizations
   */
  private static validateCrossReferences(
    config: AnalyticsConfig, 
    errors: ValidationError[], 
    warnings: ValidationError[]
  ): void {
    const allVisualizations = this.getAllVisualizations(config);
    const visualizationIds = new Set(allVisualizations.map(v => v.viz.id));

    allVisualizations.forEach(({ viz, path }) => {
      const vizConfig = viz.config as any;

      // Check sourceVisualizationId references
      if (vizConfig?.sourceVisualizationId) {
        if (!visualizationIds.has(vizConfig.sourceVisualizationId)) {
          errors.push({
            level: 'error',
            code: 'INVALID_SOURCE_VISUALIZATION_REFERENCE',
            message: `sourceVisualizationId '${vizConfig.sourceVisualizationId}' not found`,
            path: `${path}.config.sourceVisualizationId`,
            suggestion: `Available visualization IDs: ${Array.from(visualizationIds).join(', ')}`
          });
        }
      }

      // Check dateFilterId references
      if (vizConfig?.dateFilterId) {
        // Check for date-range-filter visualizations within tabs
        const hasMatchingDateFilterViz = allVisualizations.some(({ viz: otherViz }) => 
          otherViz.type === 'date-range-filter' && 
          (otherViz.config as any)?.filterId === vizConfig.dateFilterId
        );

        // Check for global filters with type: "date-range"
        const hasMatchingGlobalFilter = config.filters?.some((filter: any) => 
          filter.type === 'date-range' && 
          filter.filterId === vizConfig.dateFilterId
        );

        if (!hasMatchingDateFilterViz && !hasMatchingGlobalFilter) {
          errors.push({
            level: 'error',
            code: 'INVALID_DATE_FILTER_REFERENCE',
            message: `dateFilterId '${vizConfig.dateFilterId}' not found`,
            path: `${path}.config.dateFilterId`,
            suggestion: 'Ensure a date-range-filter visualization or global filter with type "date-range" exists with matching filterId'
          });
        }
      }

      // Check for implicit date filter dependencies (naming convention validation)
      if (['chart', 'performance-card', 'table'].includes(viz.type) && !vizConfig?.dateFilterId) {
        const availableFilterIds = allVisualizations
          .filter(({ viz: otherViz }) => otherViz.type === 'date-range-filter')
          .map(({ viz: otherViz }) => (otherViz.config as any)?.filterId)
          .filter(Boolean);

        // Check if visualization follows naming conventions that might not match available filters
        const expectedFilterPatterns = [
          `${viz.id}-date-range`,
          viz.id.replace(/-(table|chart|kpi|card)$/, '-date-range')
        ];

        const hasMatchingConventionFilter = expectedFilterPatterns.some(pattern => 
          availableFilterIds.includes(pattern)
        );

        if (!hasMatchingConventionFilter && availableFilterIds.length > 0) {
          warnings.push({
            level: 'warning',
            code: 'IMPLICIT_DATE_FILTER_MISMATCH',
            message: `No dateFilterId specified and no matching date filter found by naming convention`,
            path: `${path}.config.dateFilterId`,
            suggestion: `Consider adding dateFilterId or ensure date filter exists with ID: ${expectedFilterPatterns[1]} (available: ${availableFilterIds.join(', ')})`
          });
        }
      }
    });
  }

  /**
   * Validate best practices and performance considerations
   */
  private static validateBestPractices(
    config: AnalyticsConfig, 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    const allVisualizations = this.getAllVisualizations(config);

    // Check for too many visualizations per tab
    if (config.tabs) {
      config.tabs.forEach((tab, tabIndex) => {
        const tabVizCount = (tab.visualizations?.length || 0) + 
          (tab.subTabs?.reduce((sum, subTab) => sum + (subTab.visualizations?.length || 0), 0) || 0);

        if (tabVizCount > 20) {
          warnings.push({
            level: 'warning',
            code: 'TAB_TOO_MANY_VISUALIZATIONS',
            message: `Tab '${tab.name}' has ${tabVizCount} visualizations, consider splitting`,
            path: `config.tabs[${tabIndex}]`,
            suggestion: 'Consider using sub-tabs or splitting into multiple tabs for better performance'
          });
        }
      });
    }

    // Check for missing date filters on calculated KPIs
    const calculatedKpis = allVisualizations.filter(({ viz }) => 
      viz.type === 'kpi' && 
      ((viz.config as KpiConfig)?.mode === 'calculated' || (viz.config as KpiConfig)?.mode === 'formula')
    );

    calculatedKpis.forEach(({ viz, path }) => {
      const kpiConfig = viz.config as KpiConfig;
      if (!kpiConfig.dateFilterId) {
        info.push({
          level: 'info',
          code: 'CALCULATED_KPI_MISSING_DATE_FILTER',
          message: 'Calculated KPI without dateFilterId may not update with date changes',
          path: `${path}.config.dateFilterId`,
          suggestion: 'Add dateFilterId to ensure KPI updates when date filters change'
        });
      }
    });

    // Check for performance cards without explicit date filter configuration
    const performanceCards = allVisualizations.filter(({ viz }) => viz.type === 'performance-card');

    performanceCards.forEach(({ viz, path }) => {
      const cardConfig = viz.config as PerformanceCardConfig;
      if (!cardConfig.dateFilterId) {
        const availableFilters = allVisualizations
          .filter(({ viz: otherViz }) => otherViz.type === 'date-range-filter')
          .map(({ viz: otherViz }) => (otherViz.config as any)?.filterId)
          .filter(Boolean);

        if (availableFilters.length > 0) {
          info.push({
            level: 'info',
            code: 'PERFORMANCE_CARD_MISSING_DATE_FILTER',
            message: 'Performance card without explicit dateFilterId relies on naming conventions',
            path: `${path}.config.dateFilterId`,
            suggestion: `Consider adding explicit dateFilterId for reliable date filtering (available: ${availableFilters.join(', ')})`
          });
        }
      }
    });
  }

  /**
   * Validate data source patterns to prevent mixing incompatible configurations
   */
  private static validateDataSourcePatterns(
    config: AnalyticsConfig,
    errors: ValidationError[],
    warnings: ValidationError[]
  ): void {
    // 1. CRITICAL: Check for pattern mixing (dataSource="mock" with dataSets)
    if (config.dataSource === 'mock' && config.dataSets) {
      errors.push({
        level: 'error',
        code: 'DATASOURCE_PATTERN_MIXING',
        message: 'Cannot use both dataSource="mock" and dataSets section. These are incompatible patterns.',
        path: 'config.dataSets',
        suggestion: 'Remove dataSets section and use mockDataConfig at dashboard level for auto-generated data'
      });
    }

    // 2. CRITICAL: Check for dataSource="datasets" without dataSets
    if (config.dataSource === 'datasets' && !config.dataSets) {
      errors.push({
        level: 'error',
        code: 'DATASOURCE_MISSING_DATASETS',
        message: 'dataSource="datasets" requires a dataSets section with actual data',
        path: 'config.dataSets',
        suggestion: 'Add dataSets section with inline data, or change dataSource to "mock" and use mockDataConfig'
      });
    }

    // 3. CRITICAL: Check for mockDataConfig in wrong location (inside dataSets)
    if (config.dataSets && typeof config.dataSets === 'object') {
      Object.entries(config.dataSets).forEach(([key, dataSet]) => {
        if (dataSet && typeof dataSet === 'object' && (dataSet as any).mockDataConfig) {
          errors.push({
            level: 'error',
            code: 'MOCKDATA_WRONG_LOCATION',
            message: `mockDataConfig found inside dataSets["${key}"] - must be at dashboard level`,
            path: `config.dataSets.${key}.mockDataConfig`,
            suggestion: 'Move mockDataConfig to dashboard root level and remove dataSets section'
          });
        }
      });
    }

    // 4. CRITICAL: Check for wrong mockDataConfig structure 
    if (config.mockDataConfig && typeof config.mockDataConfig === 'object') {
      const mockConfig = config.mockDataConfig as any;
      
      // Check for "fields" instead of "schema.requiredFields"
      if (mockConfig.fields && !mockConfig.schema) {
        errors.push({
          level: 'error',
          code: 'MOCKDATA_WRONG_STRUCTURE',
          message: 'mockDataConfig should use "schema.requiredFields", not "fields"',
          path: 'config.mockDataConfig.fields',
          suggestion: 'Use structure: { "schema": { "requiredFields": [...] } } instead of { "fields": [...] }'
        });
      }

      // Check for missing strategy
      if (!mockConfig.strategy) {
        errors.push({
          level: 'error',
          code: 'MOCKDATA_MISSING_STRATEGY',
          message: 'mockDataConfig must include strategy: "generated"',
          path: 'config.mockDataConfig.strategy',
          suggestion: 'Add "strategy": "generated" to mockDataConfig'
        });
      }

      // Check for missing schema when strategy is "generated"
      if (mockConfig.strategy === 'generated' && !mockConfig.schema) {
        errors.push({
          level: 'error',
          code: 'MOCKDATA_MISSING_SCHEMA',
          message: 'mockDataConfig with strategy="generated" must include schema with requiredFields',
          path: 'config.mockDataConfig.schema',
          suggestion: 'Add "schema": { "requiredFields": [...] } to mockDataConfig'
        });
      }
    }

    // 5. WARNING: Check for dataSource="mock" without mockDataConfig
    if (config.dataSource === 'mock' && !config.mockDataConfig) {
      warnings.push({
        level: 'warning',
        code: 'DATASOURCE_MOCK_NO_CONFIG',
        message: 'dataSource="mock" should include mockDataConfig for data generation',
        path: 'config.mockDataConfig',
        suggestion: 'Add mockDataConfig with strategy="generated" and schema.requiredFields, or change dataSource to "datasets"'
      });
    }

    // 6. CRITICAL: Check for mock data dashboards using complex calculated KPI patterns
    if (config.dataSource === 'mock') {
      this.validateMockDataKpiPatterns(config, errors, warnings);
    }
  }

  /**
   * Validate KPI patterns specifically for mock data dashboards
   */
  private static validateMockDataKpiPatterns(
    config: AnalyticsConfig,
    errors: ValidationError[],
    warnings: ValidationError[]
  ): void {
    if (!config.tabs) return;

    config.tabs.forEach((tab, tabIndex) => {
      if (!tab.visualizations) return;

      tab.visualizations.forEach((viz, vizIndex) => {
        if (viz.type !== 'kpi') return;

        const vizConfig = viz.config as any;
        if (!vizConfig) return;

        const basePath = `config.tabs[${tabIndex}].visualizations[${vizIndex}].config`;

        // Check for complex primaryMetric object (should be simple string)
        if (vizConfig.primaryMetric && typeof vizConfig.primaryMetric === 'object') {
          errors.push({
            level: 'error',
            code: 'KPI_MOCK_COMPLEX_PRIMARY_METRIC',
            message: 'For mock data KPIs, primaryMetric should be a simple field name string, not an object',
            path: `${basePath}.primaryMetric`,
            suggestion: 'Use "primaryMetric": "field_name" instead of {"field": "field_name", ...}'
          });
        }

        // Check for secondaryMetrics (not supported in simple KPI pattern)
        if (vizConfig.secondaryMetrics) {
          warnings.push({
            level: 'warning',
            code: 'KPI_MOCK_SECONDARY_METRICS',
            message: 'secondaryMetrics may not work correctly with mock data. Consider using separate KPI visualizations',
            path: `${basePath}.secondaryMetrics`,
            suggestion: 'Create separate KPI visualizations for each metric instead of using secondaryMetrics'
          });
        }

        // Check for calculated mode with sourceVisualizationId (circular reference risk)
        if (vizConfig.mode === 'calculated' && vizConfig.sourceVisualizationId) {
          warnings.push({
            level: 'warning',
            code: 'KPI_MOCK_CALCULATED_CIRCULAR_RISK',
            message: 'KPI with mode="calculated" and sourceVisualizationId may create circular references in mock data dashboards',
            path: `${basePath}.mode`,
            suggestion: 'For mock data, use direct KPIs without mode="calculated" and sourceVisualizationId'
          });
        }

        // Check for missing aggregation when mode is calculated
        if (vizConfig.mode === 'calculated' && !vizConfig.aggregation) {
          errors.push({
            level: 'error',
            code: 'KPI_CALCULATED_MISSING_AGGREGATION',
            message: 'KPI with mode="calculated" must specify aggregation (sum, avg, min, max, count)',
            path: `${basePath}.aggregation`,
            suggestion: 'Add "aggregation": "sum" (or avg, min, max, count) to the KPI config'
          });
        }
      });
    });
  }

  /**
   * Validate data structure patterns for common issues
   */
  private static validateDataStructure(
    config: AnalyticsConfig, 
    errors: ValidationError[], 
    warnings: ValidationError[], 
    info: ValidationError[]
  ): void {
    if (!config.dataSets) return;

    // Check each dataset for multi-series chart compatibility issues
    Object.entries(config.dataSets).forEach(([dataSetKey, dataSet]) => {
      if (!dataSet || !dataSet.data || !Array.isArray(dataSet.data)) return;
      
      const sampleData = dataSet.data[0];
      if (!sampleData) return;

      // Detect potential wide vs long format issues
      const dataFields = Object.keys(sampleData);
      const hasDateField = dataFields.some(field => 
        field.includes('date') || field.includes('time') || field.includes('Date') || field.includes('Time')
      );
      
      // Look for charts using this dataset
      const allVisualizations = this.getAllVisualizations(config);
      const chartsUsingDataSet = allVisualizations.filter(({ viz }) => 
        viz.type === 'chart' && 
        viz.dataSet?.dataSetId === dataSetKey
      );

      chartsUsingDataSet.forEach(({ viz, path }) => {
        const chartConfig = viz.config as any;
        const hasGroupBy = viz.dataQuery?.groupBy && viz.dataQuery.groupBy.length > 0;
        const hasMultipleSeries = chartConfig?.series && chartConfig.series.length > 1;
        const isCalculatedMode = chartConfig?.mode === 'calculated';

        // Check for multi-series chart with long format data (common issue)
        if (hasMultipleSeries && hasGroupBy && !isCalculatedMode) {
          warnings.push({
            level: 'warning',
            code: 'CHART_MULTI_SERIES_LONG_FORMAT',
            message: 'Multi-series chart with groupBy data may need mode: "calculated" for proper aggregation',
            path: `${path}.config.mode`,
            suggestion: 'Add mode: "calculated" and aggregation to handle long format data properly'
          });
        }

        // Check for yAxis field that doesn't exist in data
        if (chartConfig?.yAxis?.field && !dataFields.includes(chartConfig.yAxis.field)) {
          errors.push({
            level: 'error',
            code: 'CHART_YAXIS_FIELD_MISSING',
            message: `yAxis field "${chartConfig.yAxis.field}" not found in dataset "${dataSetKey}"`,
            path: `${path}.config.yAxis.field`,
            suggestion: `Available fields: ${dataFields.join(', ')}`
          });
        }

        // Check for series fields that don't exist in data
        if (chartConfig?.series) {
          chartConfig.series.forEach((series: any, seriesIndex: number) => {
            if (series.field && !dataFields.includes(series.field)) {
              errors.push({
                level: 'error',
                code: 'CHART_SERIES_FIELD_MISSING',
                message: `Series field "${series.field}" not found in dataset "${dataSetKey}"`,
                path: `${path}.config.series[${seriesIndex}].field`,
                suggestion: `Available fields: ${dataFields.join(', ')}`
              });
            }
          });
        }
      });
    });

    // Check for exampleResponse in wrong location (should be in visualizations, not dataSets)
    Object.entries(config.dataSets).forEach(([dataSetKey, dataSet]) => {
      if (dataSet && typeof dataSet === 'object') {
        const dataSetObj = dataSet as any;
        if (dataSetObj.exampleResponse || Array.isArray(dataSetObj.data)) {
          // This is actually correct for dataSets, but check if it's conflicting with dataSource
          if (config.dataSource === 'mock') {
            errors.push({
              level: 'error',
              code: 'DATASET_INVALID_EXAMPLE_RESPONSE_LOCATION',
              message: `Found 'exampleResponse' or array data in dataSets["${dataSetKey}"] with dataSource="mock"`,
              path: `config.dataSets.${dataSetKey}`,
              suggestion: 'Move exampleResponse to the visualization config, or change dataSource to "datasets"'
            });
          }
        }
      }
    });
  }

  /**
   * Validate filter fields match mock data schema fields
   * Prevents filters from referencing non-existent fields which causes all data to be filtered out
   */
  private static validateFilterFields(
    config: AnalyticsConfig,
    errors: ValidationError[],
    warnings: ValidationError[]
  ): void {
    // Only validate if we have mockDataConfig with schema
    if (!config.mockDataConfig || !config.mockDataConfig.schema) {
      return;
    }

    const schema = config.mockDataConfig.schema;
    const schemaFields = schema.requiredFields?.map(f => f.name) || [];
    
    if (schemaFields.length === 0) {
      return; // No schema fields to validate against
    }

    // Validate dashboard-level filters
    if (config.filters && Array.isArray(config.filters)) {
      config.filters.forEach((filter: any, filterIndex: number) => {
        const filterPath = `config.filters[${filterIndex}]`;
        
        // Check if filter has a field property
        if (filter.field) {
          // For hierarchical filters, check all levels exist in schema
          if (filter.hierarchical && filter.hierarchyConfig && filter.hierarchyConfig.levels) {
            const levels = filter.hierarchyConfig.levels;
            levels.forEach((level: string, levelIndex: number) => {
              if (!schemaFields.includes(level)) {
                errors.push({
                  level: 'error',
                  code: 'FILTER_FIELD_NOT_IN_SCHEMA',
                  message: `Hierarchical filter level "${level}" not found in mockDataConfig schema`,
                  path: `${filterPath}.hierarchyConfig.levels[${levelIndex}]`,
                  suggestion: `Available schema fields: ${schemaFields.join(', ')}. Add "${level}" to mockDataConfig.schema.requiredFields or fix the level name.`
                });
              }
            });
          }
          // For non-hierarchical filters, check the field exists
          else if (!schemaFields.includes(filter.field)) {
            errors.push({
              level: 'error',
              code: 'FILTER_FIELD_NOT_IN_SCHEMA',
              message: `Filter field "${filter.field}" not found in mockDataConfig schema`,
              path: `${filterPath}.field`,
              suggestion: `Available schema fields: ${schemaFields.join(', ')}. Add "${filter.field}" to mockDataConfig.schema.requiredFields or fix the filter field name.`
            });
          }
        }
      });
    }

    // Validate hierarchy levels in hierarchyConfig match schema fields
    if (config.mockDataConfig.hierarchyConfig) {
      const hierarchyLevels = config.mockDataConfig.hierarchyConfig.levels || [];
      hierarchyLevels.forEach((level: string, levelIndex: number) => {
        if (!schemaFields.includes(level)) {
          warnings.push({
            level: 'warning',
            code: 'HIERARCHY_LEVEL_NOT_IN_SCHEMA',
            message: `Hierarchy level "${level}" not found in mockDataConfig schema`,
            path: `config.mockDataConfig.hierarchyConfig.levels[${levelIndex}]`,
            suggestion: `This may cause mock data generation to fail. Add "${level}" to schema.requiredFields with type "enum" and appropriate enumValues.`
          });
        }
      });

      // Validate relationships reference valid hierarchy levels
      if (config.mockDataConfig.hierarchyConfig.relationships) {
        const relationships = config.mockDataConfig.hierarchyConfig.relationships;
        Object.keys(relationships).forEach(parentLevel => {
          if (!hierarchyLevels.includes(parentLevel)) {
            warnings.push({
              level: 'warning',
              code: 'HIERARCHY_RELATIONSHIP_INVALID_PARENT',
              message: `Relationship parent level "${parentLevel}" not in hierarchy levels`,
              path: `config.mockDataConfig.hierarchyConfig.relationships.${parentLevel}`,
              suggestion: `Add "${parentLevel}" to hierarchyConfig.levels or remove this relationship.`
            });
          }

          // Check child levels in relationships
          const parentRelationships = relationships[parentLevel];
          Object.values(parentRelationships).forEach((childData: any) => {
            Object.keys(childData).forEach(childLevel => {
              if (!hierarchyLevels.includes(childLevel)) {
                warnings.push({
                  level: 'warning',
                  code: 'HIERARCHY_RELATIONSHIP_INVALID_CHILD',
                  message: `Relationship child level "${childLevel}" not in hierarchy levels`,
                  path: `config.mockDataConfig.hierarchyConfig.relationships.${parentLevel}.${childLevel}`,
                  suggestion: `Add "${childLevel}" to hierarchyConfig.levels or remove this relationship.`
                });
              }
            });
          });
        });
      }
    }
  }

  /**
   * Helper to get all visualizations with their paths
   */
  private static getAllVisualizations(config: AnalyticsConfig): Array<{ viz: AnalyticsVisualization, path: string }> {
    const result: Array<{ viz: AnalyticsVisualization, path: string }> = [];

    config.tabs?.forEach((tab, tabIndex) => {
      const tabPath = `config.tabs[${tabIndex}]`;

      tab.visualizations?.forEach((viz, vizIndex) => {
        result.push({ viz, path: `${tabPath}.visualizations[${vizIndex}]` });
      });

      tab.subTabs?.forEach((subTab, subTabIndex) => {
        const subTabPath = `${tabPath}.subTabs[${subTabIndex}]`;
        subTab.visualizations?.forEach((viz, vizIndex) => {
          result.push({ viz, path: `${subTabPath}.visualizations[${vizIndex}]` });
        });
      });
    });

    return result;
  }

  /**
   * Format validation results for display
   */
  static formatValidationResults(result: ValidationResult): string {
    const lines: string[] = [];

    if (result.isValid) {
      lines.push('✅ Configuration is valid!');
    } else {
      lines.push('❌ Configuration has errors:');
    }

    if (result.errors.length > 0) {
      lines.push('\n🚫 ERRORS:');
      result.errors.forEach(error => {
        lines.push(`  • ${error.message} (${error.path})`);
        if (error.suggestion) {
          lines.push(`    💡 ${error.suggestion}`);
        }
      });
    }

    if (result.warnings.length > 0) {
      lines.push('\n⚠️ WARNINGS:');
      result.warnings.forEach(warning => {
        lines.push(`  • ${warning.message} (${warning.path})`);
        if (warning.suggestion) {
          lines.push(`    💡 ${warning.suggestion}`);
        }
      });
    }

    if (result.info.length > 0) {
      lines.push('\nℹ️ SUGGESTIONS:');
      result.info.forEach(info => {
        lines.push(`  • ${info.message} (${info.path})`);
        if (info.suggestion) {
          lines.push(`    💡 ${info.suggestion}`);
        }
      });
    }

    return lines.join('\n');
  }
} 